/**
 * Raster choice for the art pack. Avif, then webp, then the original png or jpg.
 * The browser is probed once before the loader starts, so the first fetch asks
 * for a single encoding.
 */

export type RasterFormat = 'avif' | 'webp' | 'original';

let current: RasterFormat = 'original';

export function setRasterFormat(format: RasterFormat): void {
  current = format;
}

export function rasterFormat(): RasterFormat {
  return current;
}

export function chooseRasterFormat(support: { avif: boolean; webp: boolean }): RasterFormat {
  if (support.avif) return 'avif';
  if (support.webp) return 'webp';
  return 'original';
}

/** `assets/ui/panel.png` + avif → `assets/ui/panel.avif`. Other paths stay put. */
export function rasterUrl(path: string, format: RasterFormat = current): string {
  if (format === 'original') return path;
  return path.replace(/\.(png|jpe?g)$/i, `.${format}`);
}

/** 2×2 still AVIF, produced by avifenc. Used only to probe decode support. */
const AVIF_PIXEL =
  'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUEAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB8AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgSAAAAAAABNjb2xybmNseAABAA0ABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACdtZGF0EgAKBzgANhAQ0GkyEhgACiiihAAAsBNWZDGABE4fIA==';

/** 2×2 webp. */
const WEBP_PIXEL =
  'data:image/webp;base64,UklGRjwAAABXRUJQVlA4IDAAAADQAQCdASoCAAIAAUAmJaACdLoB+AADsAD+8ut//NgVzXPv9//S4P0uD9Lg/9KQAAA=';

function decodes(src: string): Promise<boolean> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image.naturalWidth > 0);
    image.onerror = () => resolve(false);
    image.src = src;
  });
}

export async function detectRasterFormat(): Promise<RasterFormat> {
  if (typeof Image === 'undefined') return 'original';
  const avif = await decodes(AVIF_PIXEL);
  if (avif) return 'avif';
  const webp = await decodes(WEBP_PIXEL);
  return chooseRasterFormat({ avif: false, webp });
}
